import ProfileAlertService from "@services/profile-alert-service";
import { getSingleInstanceByElement, initialiseComponents } from "../../core";
import BinderAutocomplete from "../binder/binder-autocomplete";
import Component from "../component.abstract";
import { notifyFromAlert } from "../helpers/notifs";
import SuggestInput from "../inputs/autocomplete/autocomplete";
import Selector from "../multiselect/select";
import AntiForgeryService from "@services/anti_forgery/antiForgery_service";
import AlertService from "@services/alert/alert_service";

interface ProfileAlertProps {
  alert: Alert;
  noAlerts: boolean;
  url: string;
}

interface Alert {
  [index: string]: any;
  id?: string;
  functionsOptions: any;
  contractsOptions: any;
  name: string;
  locality: string;
  contracts: any[];
  functions: any[];
  frequency: string;
  open?: boolean;
}

interface Payload {
    id: number | string,
    name: string,
    keyword: string,
    localities: any,
    jobs: any,
    contracts: any[],
    frequency: string,
    isActive: null,
    company?: string,
    idCompteProduit?: number
}

interface Notification {
  Icon: number;
  Message: string;
  AnalyticsTrackings: string;
  sticky: boolean;
}

export default class ProfileAlert extends Component {
  private id: string;
  private headerNameField: HTMLElement;
  private editNameField: HTMLInputElement;
  private headerTags: HTMLElement;
  private headerFrequencyField: HTMLElement;
  private editFrequencyFieldDay: HTMLInputElement;
  private editFrequencyFieldWeek: HTMLInputElement;
  private saveButton: HTMLElement;
  private removeButton: HTMLElement;
  private seeOffersButton: HTMLElement;
  private toggleCheckbox: HTMLInputElement;
  private contractsSelect: Selector;
  private localityInput: SuggestInput;
  private localityBinder: BinderAutocomplete;
  private jobInput: SuggestInput;
  private jobBinder: BinderAutocomplete;
  private keywordField: HTMLInputElement;
  private isNew: boolean;
  private alertService: AlertService
  private antiForgeryService: AntiForgeryService

  constructor(private props: ProfileAlertProps, element: HTMLElement) {
    super(props, element);
    this.alertService = new AlertService()
    this.antiForgeryService = new AntiForgeryService()
  }

  mounted(): void {
    this.injectTemplate({initializeComponentAfterInject : false });

    if (this.props.alert.id) {
        this.id = this.props.alert.id;
    } else {
        this.id = "alert_" + Math.floor(Math.random() * 1000);
        this.element.classList.add("create");
    }

    this.initRadioButtons();
    this.addOptions("contracts");
 
    // Init fields
    this.headerNameField = this.element.querySelector(
      ".profile-alert-header > div:first-of-type > div:first-of-type > span"
    );
    this.editNameField = this.element.querySelector(
      ".profile-alert-details > input"
    );
    this.headerTags = this.element.querySelector(
      ".profile-alert-header > div:first-of-type > div:nth-of-type(2) > ul"
    );
    this.headerFrequencyField = this.element.querySelector(
      ".profile-alert-header > div:first-of-type > div:nth-of-type(2) > div"
    );
    this.editFrequencyFieldDay = this.element.querySelector(
      ".profile-alert-details > .profile-alert-frequency > input[value=everyday]"
    );
    this.editFrequencyFieldWeek = this.element.querySelector(
      ".profile-alert-details > .profile-alert-frequency > input[value=everyweek]"
    );
    this.keywordField = this.element.querySelector(
      ".profile-alert-details > .profile-alert-criterias input[name=keyword]"
    );

    // Init buttons
    this.saveButton = this.element.querySelector(".profile-alert-save");
    this.removeButton = this.element.querySelector(".profile-remove-alert");
    this.seeOffersButton = this.element.querySelector(".profile-see-offers");
    this.toggleCheckbox = this.element.querySelector(
      ".profile-alert-header input[type=checkbox]"
    );

    this.initSaveButton();
    this.initRemoveButton();
    this.initSeeOffersButton();
    this.initToggleButton();

    this.initTracking();

    if (this.props.noAlerts) {
      this.element.classList.add("no-alerts");
    }
    initialiseComponents();
    this.contractsSelect = getSingleInstanceByElement(
      "Selector",
      this.element.querySelector("select[name=contracts]")
    );
    this.localityInput = getSingleInstanceByElement(
      "SuggestInput",
      this.element.querySelector("input[data-autocomplete][name=locality]")
    );
    this.jobInput = getSingleInstanceByElement(
      "SuggestInput",
      this.element.querySelector("input[data-autocomplete][name=job]")
    );
    this.jobBinder = getSingleInstanceByElement(
      "BinderAutocomplete",
      this.element.querySelector(
        "[data-binder-autocomplete][data-name=job]"
      )
    );
    this.localityBinder = getSingleInstanceByElement(
      "BinderAutocomplete",
      this.element.querySelector(
        "[data-binder-autocomplete][data-name=locality]"
      )
    );
  
    // Init tooltips
    const infoInputPath =
      ".profile-alert-details > div:first-of-type > input[type=checkbox]";
    const infoInput: HTMLInputElement =
      this.element.querySelector(infoInputPath);
    this.initCheckbox("_info", infoInputPath, " + div > label");

    let infoTooltipTimeout: NodeJS.Timeout;
    infoInput.addEventListener("change", () => {
      clearTimeout(infoTooltipTimeout);
      const tooltip = this.element.querySelector("[data-id=info-tooltip]");
      tooltip.classList.toggle("closed");
      if (!tooltip.classList.contains("closed")) {
        this.editNameField.focus();

        infoTooltipTimeout = setTimeout(() => {
          // If the tooltip is still opened, close it
          if (!tooltip.classList.contains("closed")) {
            tooltip.classList.add("closed");
          }
        }, 5000);
      }
    });

    const jobInputPath = "[data-id=job-tooltip-input]";
    const jobInput: HTMLInputElement = this.element.querySelector(jobInputPath);
    this.initCheckbox("_job_info", jobInputPath, " + div > label");

    let jobTooltipTimeout: NodeJS.Timeout;
    jobInput.addEventListener("change", () => {
      clearTimeout(jobTooltipTimeout);
      const tooltip = this.element.querySelector("[data-id=job-tooltip]");
      tooltip.classList.toggle("closed");
      if (!tooltip.classList.contains("closed")) {
        this.jobInput.focus();

        jobTooltipTimeout = setTimeout(() => {
          // If the tooltip is still opened, close it
          if (!tooltip.classList.contains("closed")) {
            tooltip.classList.add("closed");
          }
        }, 5000);
      }
    });

    const enabledInputPath =
      ".profile-alert-header > div:first-of-type > div:first-of-type > input[type=checkbox]";
    this.initCheckbox("_enabled", enabledInputPath, " + label");

    // Init edit button
    const editButton = this.element.querySelector(".profile-edit-alert");
    editButton.addEventListener("click", () => {
      this.element.classList.toggle("edit");
    });

    this.setFields();

    if (this.props.alert.open) {
      editButton.dispatchEvent(new Event("click"));
    }   
  }
  // Set unique names and IDs for the radio buttons
  initRadioButtons(): void {
    const radioSelector = ".profile-alert-details input[type=radio]";
    const radioButtons = this.element.querySelectorAll(radioSelector);
    for (let i = 0; i < radioButtons.length; i++) {
      const radioButton = radioButtons[i];
      const name = this.id + "_frequency";
      const id = name + "_" + i;
      radioButton.setAttribute("name", name);
      radioButton.setAttribute("id", id);
      this.element
        .querySelector(radioSelector + " + label:nth-of-type(" + (i + 1) + ")")
        .setAttribute("for", id);
    }
  }
  // Set unique names and IDs for the checkboxes
  initCheckbox(id: string, path: string, labelPath: string): void {
    const input = this.element.querySelector(path);
    const inputId = this.id + "_" + id;
    input.setAttribute("name", inputId);
    input.setAttribute("id", inputId);
    this.element.querySelector(path + labelPath).setAttribute("for", inputId);
  }
  addOptions(name: string): void {
    const select = this.element.querySelector("select[name=" + name + "]");
    const options = this.props.alert[name + "Options"];

    if (options.length) {
      for (let i = 0; i < options.length; i++) {
        const o = options[i];
        const option = document.createElement("option");
        option.setAttribute("value", o.uri);
        option.innerHTML = o.label;
        select.appendChild(option);
      }
    } else {
      // Hide the select and its title
      select.classList.add("hidden");
      select.previousElementSibling.classList.add("hidden");
    }
  }
  initSaveButton(): void {

    this.saveButton.addEventListener("click", () => {
      if (this.getErrors()) {
        return;
      }
      this.saveButton.classList.add("disabled");

      this.isNew = !this.props.alert.id;

      if (this.isNew) {
        this.antiForgeryService.getToken()
            .then((token: string): void => {
              this.alertService.createAlert(this.getFields(this.isNew), token)
                  .then(alert => this.handleResponse(alert))
                  .catch((error) => {
                    this.displayError(error)
                    this.saveButton.classList.remove("disabled");
                  })
            })
      } else {
        this.alertService.updateAlert(this.getFields(this.isNew))
            .then(alert => this.handleResponse(alert))
            .catch((error) => {
              this.displayError(error)
              this.saveButton.classList.remove("disabled");
            })
      }
    });
  }

  handleResponse(response: any): void {
    if (response.antiForgeryError) {
      this.displayError()
    } else {
      const contractsOptions = this.props.alert.contractsOptions;

      this.props.alert = response;
      this.id = this.props.alert.id;

      this.props.alert.contractsOptions = contractsOptions;

      this.setFields();

      // Close edition form
      this.element.classList.remove("edit", "create");

      if (this.isNew) {
        this.props.noAlerts = false;
        this.element.classList.remove("no-alerts");
        PubSub.publish("profile-alert.created");
        this.initTracking();

        this.displayNotification({
          Icon: 0,
          Message:
              "Parfait, votre alerte a bien été créée. Dès la parution de nouvelles offres, vous serez alerté par email.",
          AnalyticsTrackings: "",
          sticky: true,
        });
      } else {
        this.displayNotification({
          Icon: 0,
          Message: "Votre alerte a bien été modifiée.",
          AnalyticsTrackings: "",
          sticky: true,
        });
      }
    }

    this.saveButton.classList.remove("disabled");
  }
  initRemoveButton(): void {
    this.removeButton.addEventListener("click", () => {
      this.alertService.deleteAlert(this.id)
          .then(() => {
            this.element.remove()
            this.displayNotification({
              Icon: 0,
              Message: "Votre alerte a bien été supprimée.",
              AnalyticsTrackings: "",
              sticky: true,
            })
          })
          .catch(() => {
            this.displayError()
          })
    });
  }
  initSeeOffersButton(): void {
    if (this.props.alert.nbOffers && +this.props.alert.nbOffers) {
      this.seeOffersButton.innerHTML =
        "Voir les " + this.props.alert.nbOffers + " offres";
    }

    this.seeOffersButton.addEventListener("click", () => {
      window.location = this.props.alert.url;
    });
  }
  initToggleButton(): void {
    this.toggleCheckbox.checked = this.props.alert.isActive;

    this.toggleCheckbox.addEventListener("change", () => {
      const data = {
        id: this.id,
        isActive: this.toggleCheckbox.checked,
      };
      this.antiForgeryService.getToken().then((token) => {
        this.alertService.toggleAlert(data, token)
            .then((response) => this.handleToggleResponse(response))
            .catch(() => this.displayError())
      })
    });
  }
  handleToggleResponse (response: any) {
    if (response.antiForgeryError) {
      this.displayError(response.message)
    } else {
      if (this.props.alert.trackingsClick && this.props.alert.trackingsClick.length) {
        if (this.toggleCheckbox.checked) {
          this.props.alert.trackingsClick[2].EventLibelle = "[Jobalerte] - Activer";
          window.dataLayer.push(this.props.alert.trackingsClick[2]);
        } else {
          this.props.alert.trackingsClick[2].EventLibelle = "[Jobalerte] - Désactiver";
          window.dataLayer.push(this.props.alert.trackingsClick[2]);
        }
      }
    }
  }
  initTracking(): void {
    if (
      this.props.alert.trackingsLoad &&
      this.props.alert.trackingsLoad.length
    ) {
      const eventAnalytics = this.props.alert.trackingsLoad[0];
      eventAnalytics.trigger = "init";
      this.element.setAttribute("data-track", JSON.stringify(eventAnalytics));
    }

    if (
      this.props.alert.trackingsClick &&
      this.props.alert.trackingsClick.length
    ) {
      // Save button
      this.saveButton.setAttribute(
        "data-track",
        JSON.stringify(this.props.alert.trackingsClick[0])
      );

      // Remove button
      this.removeButton.setAttribute(
        "data-track",
        JSON.stringify(this.props.alert.trackingsClick[1])
      );

      // See offers button
      this.seeOffersButton.setAttribute(
        "data-track",
        JSON.stringify(this.props.alert.trackingsClick[3])
      );
    }
  }
  
  getFields(isNew: boolean): Payload {
    let frequency: string;
    if (this.editFrequencyFieldDay.checked) {
      frequency = "everyday";
    } else if (this.editFrequencyFieldWeek.checked) {
      frequency = "everyweek";
    }

    const fields: Payload = {
      id: isNew ? null : this.id,
      name: this.editNameField.value,
      keyword: this.keywordField.value,
      localities: this.localityBinder.getValuesAndLabels(),
      jobs: this.jobBinder.getValuesAndLabels(),
      contracts: this.contractsSelect.getValue(),
      frequency: frequency,
      isActive: null
    };

    if (this.props.alert.company) {
      fields.company = this.props.alert.company;
    }

    if (this.props.alert.idCompteProduit) {
      fields.idCompteProduit = this.props.alert.idCompteProduit;
    }

    return fields
  }
  setFields(): void {
    if (this.props.alert.name) {
      this.headerNameField.innerText = this.props.alert.name;
      this.editNameField.value = this.props.alert.name;
    }

    this.toggleCheckbox.checked = this.props.alert.isActive;

    if (this.props.alert.frequency === "everyday") {
      this.headerFrequencyField.innerHTML =
        "Une fois par jour dans ma boîte mail";
      this.editFrequencyFieldDay.checked = true;
    } else if (this.props.alert.frequency === "everyweek") {
      this.headerFrequencyField.innerHTML =
        "Une fois par semaine dans ma boîte mail";
      this.editFrequencyFieldWeek.checked = true;
    }

    this.selectOptions("contracts");

    if (this.props.alert.localities) {
      for (const id in this.props.alert.localities) {
        if (
          Object.prototype.hasOwnProperty.call(this.props.alert.localities, id)
        ) {
          this.localityBinder.addValue(undefined, {
            element: {
              value: this.props.alert.localities[id],
            },
            autocompleteinput: {
              value: id,
            },
          });
        }
      }
    }

    if (this.props.alert.jobs) {
      for (const id in this.props.alert.jobs) {
        if (
          Object.prototype.hasOwnProperty.call(this.props.alert.jobs, id)
        ) {
          this.jobBinder.addValue(undefined, {
            element: {
              value: this.props.alert.jobs[id],
            },
            autocompleteinput: {
              value: id,
            },
          });
        }
      }
    }

    if (this.props.alert.keyword) {
      this.keywordField.value = this.props.alert.keyword;
    }

    if (this.props.alert.company) {
      this.element.querySelector("[data-id=company-criteria]").classList.remove("hidden");
      (<HTMLInputElement> this.element.querySelector("[data-id=company-input]")).value = this.props.alert.company;
    }

    this.addTags();
  }
  addTags(): void {
    this.headerTags.innerHTML = "";

    let nbTags = 0;
    const localities: string[] = this.props.alert.localities ? Object.values(this.props.alert.localities) : [];
    const jobs: string[] = this.props.alert.jobs ? Object.values(this.props.alert.jobs): [];

    // Add the locality tag if needed
    if (localities.length > 0) {
      const li = document.createElement("li");
      if (localities.length > 1) {
        li.innerHTML = localities.length + " localités";
      } else {
        li.innerHTML = localities[0];
      }
      this.headerTags.appendChild(li);
      nbTags++;
    }

    // Add the job tag if needed
    if (jobs.length > 0) {
      const li = document.createElement("li");
      if (jobs.length > 1) {
        li.innerHTML = jobs.length + " métiers";
      } else {
        li.innerHTML = jobs[0];
      }
      this.headerTags.appendChild(li);
      nbTags++;
    }

    // Add the contract tag if needed (no more thant 2 tags)
    if (
      nbTags < 2 &&
      this.props.alert.contracts &&
      this.props.alert.contracts.length
    ) {
      this.addTag("contracts", "contrats");
    }
  }

  addTag(tag: string, plural: string): void {
    const li = document.createElement("li");
    if (this.props.alert[tag].length > 1) {
      li.innerHTML = this.props.alert[tag].length;
      li.innerHTML += " " + plural;
    } else {
      const options = this.props.alert[tag + "Options"];
      for (let i = 0; i < options.length; i++) {
        if (options[i].uri === this.props.alert[tag][0]) {
          li.innerHTML = options[i].label;
          break;
        }
      }
    }
    this.headerTags.appendChild(li);
  }

  getErrors(): boolean {
    let haveError = false;

    if (!this.editNameField.value) {
      haveError = true;
    }

    // No need to validate these fields for company alerts
    if (!this.props.alert.idCompteProduit) {
      if (JSON.stringify(this.localityBinder.getValuesAndLabels()) === "{}") {
        // The locality input is filled but no value was selected
        haveError = true;
      }

      if ((JSON.stringify(this.jobBinder.getValuesAndLabels()) === "{}" && !this.keywordField.value) ||
        !this.contractsSelect.getValue().length) {
          // We need (job || keyword) && contract
          haveError = true;
      }
    }

    if (haveError) {
      this.displayError(
        "Oups, un nom d'alerte, une localité, un métier et un type de contrat sont nécessaires pour vous envoyer des offres d’emploi pertinentes."
      );
      window.scrollTo({top: 0, behavior: 'smooth'});
    }  
    
    return haveError;
  }

  displayError(error?: string): void {
    let message =
      "Aïe, une erreur est survenue, rechargez la page et réessayez.";
    if (error) {
      message = error;
    }

    this.displayNotification({
      Icon: 2,
      Message: message,
      AnalyticsTrackings: "",
      sticky: true,
    });
  }
  displayNotification(notification: Notification): void {
    notifyFromAlert(notification, true);
    if (window.innerWidth < 767) {
      window.scrollTo(0, 0);
    }
  }
  selectOptions(select: any): void {
    if (select === "contracts") {
        this.contractsSelect.unselectAll();
    }
  
    if (this.props.alert[select] && this.props.alert[select].length) {
      for (let i = 0; i < this.props.alert[select].length; i++) {
        const option = this.element.querySelector(
          "select[name=" +
            select +
            '] option[value="' +
            this.props.alert[select][i] +
            '"]'
        );
        if (option) {
          if (select === "contracts") {
            this.contractsSelect.selectOption(option);
          }
        }
      }
    }
  }
}
